import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { Toaster } from 'sonner';

import { SiteHeader } from './components/SiteHeader';
import { SiteFooter } from './components/SiteFooter';
import { AdminShell } from './components/AdminShell';

import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Support from './pages/Support';
import Clients from './pages/Clients';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';

import Store from './pages/Store';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';

import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import OrderDetail from './pages/OrderDetail';

import Dashboard from './pages/admin/Dashboard';
import Products from './pages/admin/Products';
import ProductForm from './pages/admin/ProductForm';
import Categories from './pages/admin/Categories';
import AdminUsers from './pages/admin/Users';
import AdminOrders from './pages/admin/Orders';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import Inventory from './pages/admin/Inventory';
import Discounts from './pages/admin/Discounts';
import BillingManagement from './pages/admin/BillingManagement';
import Returns from './pages/admin/Returns';
import NDR from './pages/admin/NDR';
import Pickups from './pages/admin/Pickups';
import ShippingAnalytics from './pages/admin/ShippingAnalytics';
import Analytics from './pages/admin/Analytics';

import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Cargando...</div>;
  if (!user) return <Navigate to="/login" />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Cargando...</div>;
  if (!user) return <Navigate to="/login" />;
  if (!isAdmin) return <Navigate to="/" />;
  return children;
}

function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <main className="flex-1">{children}</main>
      <SiteFooter />
    </div>
  );
}

function AdminLayout({ children }) {
  return (
    <AdminShell>{children}</AdminShell>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0);
  }
  return null;
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public Pages */}
        <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
        <Route path="/nosotros" element={<PublicLayout><About /></PublicLayout>} />
        <Route path="/servicios" element={<PublicLayout><Services /></PublicLayout>} />
        <Route path="/soporte" element={<Navigate to="https://soporte.kindtechnologies.net/portal/es/home" />} />
        <Route path="/clientes" element={<PublicLayout><Clients /></PublicLayout>} />
        <Route path="/contacto" element={<PublicLayout><Contact /></PublicLayout>} />
        <Route path="/terminos" element={<PublicLayout><Terms /></PublicLayout>} />
        <Route path="/privacidad" element={<PublicLayout><Privacy /></PublicLayout>} />

        {/* Store */}
        <Route path="/tienda" element={<PublicLayout><Store /></PublicLayout>} />
        <Route path="/tienda/:id" element={<PublicLayout><ProductDetail /></PublicLayout>} />

        {/* Auth */}
        <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
        <Route path="/registro" element={<PublicLayout><Register /></PublicLayout>} />
        <Route path="/forgot-password" element={<PublicLayout><ForgotPassword /></PublicLayout>} />
        <Route path="/reset-password" element={<PublicLayout><ResetPassword /></PublicLayout>} />

        {/* Protected User Pages */}
        <Route path="/carrito" element={<PublicLayout><ProtectedRoute><Cart /></ProtectedRoute></PublicLayout>} />
        <Route path="/checkout" element={<PublicLayout><ProtectedRoute><Checkout /></ProtectedRoute></PublicLayout>} />
        <Route path="/perfil" element={<PublicLayout><ProtectedRoute><Profile /></ProtectedRoute></PublicLayout>} />
        <Route path="/pedido/:id" element={<PublicLayout><ProtectedRoute><OrderDetail /></ProtectedRoute></PublicLayout>} />

        {/* Admin */}
        <Route path="/admin" element={<AdminRoute><AdminLayout><Dashboard /></AdminLayout></AdminRoute>} />
        <Route path="/admin/productos" element={<AdminRoute><AdminLayout><Products /></AdminLayout></AdminRoute>} />
        <Route path="/admin/productos/nuevo" element={<AdminRoute><AdminLayout><ProductForm /></AdminLayout></AdminRoute>} />
        <Route path="/admin/productos/:id" element={<AdminRoute><AdminLayout><ProductForm /></AdminLayout></AdminRoute>} />
        <Route path="/admin/categorias" element={<AdminRoute><AdminLayout><Categories /></AdminLayout></AdminRoute>} />
        <Route path="/admin/usuarios" element={<AdminRoute><AdminLayout><AdminUsers /></AdminLayout></AdminRoute>} />
        <Route path="/admin/ordenes" element={<AdminRoute><AdminLayout><AdminOrders /></AdminLayout></AdminRoute>} />
        <Route path="/admin/ordenes/:id" element={<AdminRoute><AdminLayout><AdminOrderDetail /></AdminLayout></AdminRoute>} />
        <Route path="/admin/inventario" element={<AdminRoute><AdminLayout><Inventory /></AdminLayout></AdminRoute>} />
        <Route path="/admin/descuentos" element={<AdminRoute><AdminLayout><Discounts /></AdminLayout></AdminRoute>} />
        <Route path="/admin/facturacion" element={<AdminRoute><AdminLayout><BillingManagement /></AdminLayout></AdminRoute>} />
        <Route path="/admin/devoluciones" element={<AdminRoute><AdminLayout><Returns /></AdminLayout></AdminRoute>} />
        <Route path="/admin/ndr" element={<AdminRoute><AdminLayout><NDR /></AdminLayout></AdminRoute>} />
        <Route path="/admin/recolecciones" element={<AdminRoute><AdminLayout><Pickups /></AdminLayout></AdminRoute>} />
        <Route path="/admin/analitica-envios" element={<AdminRoute><AdminLayout><ShippingAnalytics /></AdminLayout></AdminRoute>} />
        <Route path="/admin/analitica-web" element={<AdminRoute><AdminLayout><Analytics /></AdminLayout></AdminRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <CurrencyProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors closeButton />
          </CurrencyProvider>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
